import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TradingPairs } from './components/TradingPairs';
import { TradingActivity } from './components/TradingActivity';
import { OrderForm } from './components/OrderForm';
import { OrderFormContent } from './components/OrderFormContent';
import { OrderFormDialog } from './components/OrderFormDialog';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Loader2,
    Menu,
    X,
    ChevronRight,
    ChevronLeft,
    ChevronUp,
    ChevronDown,
    TrendingUp,
    TrendingDown,
    History,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { AnimatePresence, motion } from 'framer-motion';

// API Hooks
import { useGetBalance } from '@/api/balance/use-get-balance';
import { useOrders } from '@/api/orders/use-orders';
import { TradingAccount, useTradingAccounts } from '@/api/trading-accounts/use-trading-accounts';
import { useAllPrices } from '@/hooks/useAllPrices';
import { useCurrentInstrumentPrice } from '@/hooks/useCurrentInstrumentPrice';
import { useSystemStatus } from '@/api/system-configuration/use-system-configuration';
import { useInstrument, useTraderInstruments } from '@/api/instruments/use-instruments';

// Storage utilities
import {
    clearTradingTabsState,
    loadTradingTabsState,
    saveTradingTabsState,
    TradingTab,
    validateAndCleanTradingTabs,
} from '@/utils/storage';
import KLineProChart from '@/components/KLineProChart.tsx';
import { getInstrumentMarketStatus } from '@/utils/marketStatus';

// Mobile Bottom Sheet Component
const MobileBottomSheet = ({
    isOpen,
    onClose,
    children,
    title,
}: {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title: string;
}) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed inset-x-0 bottom-0 top-12 z-50 bg-background rounded-t-[10px] overflow-hidden flex flex-col shadow-xl"
                    >
                        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
                            <h2 className="text-lg font-semibold">{title}</h2>
                            <Button variant="ghost" size="icon" onClick={onClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                        <div className="flex-1 overflow-y-auto bg-background">{children}</div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

const TradingPage = () => {
    const { t } = useTranslation('trading');
    const { t: tCommon } = useTranslation('common');
    const navigate = useNavigate();

    // Initialize state with persistence logic
    const [chartTabs, setChartTabs] = useState<TradingTab[]>([]);
    const [activeTab, setActiveTab] = useState<string>('');
    const [isInitialized, setIsInitialized] = useState(false);

    const [selectedTradingAccount, setSelectedTradingAccount] = useState<string>('');
    const [realTimePrices, setRealTimePrices] = useState<
        Record<string, { bid: number; ask: number; symbol: string }>
    >({});
    const [isChangingPair, setIsChangingPair] = useState<boolean>(false);
    const [isOrderFormDialogOpen, setIsOrderFormDialogOpen] = useState<boolean>(false);
    const [selectedDirection, setSelectedDirection] = useState<'buy' | 'sell'>('buy');

    // Sidebar and tabs visibility state
    const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(() => {
        const saved = localStorage.getItem('trading_sidebar_visible');
        return saved ? saved === 'true' : true;
    });
    const [isTabsVisible, setIsTabsVisible] = useState<boolean>(() => {
        const saved = localStorage.getItem('trading_tabs_visible');
        return saved ? saved === 'true' : true;
    });

    // Mobile States
    const [isLandscape, setIsLandscape] = useState(false);
    const [mobileActiveModal, setMobileActiveModal] = useState<null | 'buy' | 'sell' | 'trades'>(
        null
    );

    // Orientation Check
    useEffect(() => {
        const checkOrientation = () => {
            setIsLandscape(window.matchMedia('(orientation: landscape)').matches);
        };

        checkOrientation();
        window.addEventListener('resize', checkOrientation);
        return () => window.removeEventListener('resize', checkOrientation);
    }, []);

    // API Hooks
    const { data: systemStatus } = useSystemStatus();
    const { data: tradingAccountsData } = useTradingAccounts();
    const { data: ordersData, refetch: refetchOrders } = useOrders();
    const { refetch: refetchBalance } = useGetBalance(selectedTradingAccount);

    // Initialize tabs state from localStorage only
    useEffect(() => {
        if (isInitialized) return;

        const savedState = loadTradingTabsState();

        if (savedState) {
            // Check if saved state contains the old default instrument
            const hasOldDefault = savedState.tabs.some(
                tab =>
                    tab.instrumentId === 'default-eur-usd' ||
                    tab.instrumentId === '00000000-0000-0000-0000-000000000000'
            );

            if (hasOldDefault) {
                console.log('Found old default instrument in localStorage, clearing it');
                clearTradingTabsState();
                // Don't set any tabs - wait for real instruments
            } else {
                setChartTabs(savedState.tabs);
                setActiveTab(savedState.activeTab);
            }
        }
        // If no saved state or old default was cleared, wait for instruments to load

        setIsInitialized(true);
    }, [isInitialized]);

    // Set default trading account when data loads
    useEffect(() => {
        if (
            tradingAccountsData?.accounts &&
            tradingAccountsData.accounts.length > 0 &&
            !selectedTradingAccount
        ) {
            setSelectedTradingAccount(tradingAccountsData.accounts[0].id);
        }
    }, [tradingAccountsData, selectedTradingAccount]);

    // Initialize default tabs if needed (fallback when no instruments available)
    useEffect(() => {
        if (!isInitialized) return;

        const savedState = loadTradingTabsState();

        if (savedState) {
            // Validate saved tabs and clean up invalid ones
            try {
                const validatedState = validateAndCleanTradingTabs(savedState, []);

                if (validatedState) {
                    setChartTabs(validatedState.tabs);
                    setActiveTab(validatedState.activeTab);

                    // Save the cleaned state if it changed
                    if (JSON.stringify(validatedState) !== JSON.stringify(savedState)) {
                        saveTradingTabsState(validatedState);
                    }
                } else {
                    throw new Error('Validation returned null');
                }
            } catch (error) {
                console.log('Validation failed, will wait for real instruments to load');
                // Don't create fallback tabs - wait for real instruments
            }
        }
        // Don't create fallback tabs - wait for real instruments
    }, [isInitialized]);

    // Save tabs state whenever it changes
    useEffect(() => {
        if (!isInitialized || chartTabs.length === 0 || !activeTab) return;

        saveTradingTabsState({ tabs: chartTabs, activeTab });
    }, [chartTabs, activeTab, isInitialized]);

    // Get the currently selected trading account
    const getSelectedAccount = (): TradingAccount | null => {
        if (!tradingAccountsData?.accounts || !selectedTradingAccount) {
            return null;
        }
        return tradingAccountsData.accounts.find(acc => acc.id === selectedTradingAccount) || null;
    };

    // Get current pair information
    const getCurrentPair = () => {
        const currentTab = chartTabs.find(tab => tab.id === activeTab);

        if (!currentTab) {
            // Return null if no tabs exist yet
            return null;
        }

        return {
            instrumentId: currentTab.instrumentId,
            symbol: currentTab.symbol,
            price_precision: 5, // Default precision for Forex
            quantity_precision: 2, // Default quantity precision
            base_asset: { symbol: 'EUR' },
            quote_asset: { symbol: 'USD' },
        };
    };

    // Get current pair for hooks
    const currentPair = getCurrentPair();

    // Get all visible instruments for price fetching (not just open tabs)
    const { data: visibleInstrumentsResponse, isLoading: isInstrumentsLoading } =
        useTraderInstruments({
            per_page: 1000, // Match TradingPairs so tab validation covers all instruments
            page: 1,
        });

    const { data: currentInstrument } = useInstrument(currentPair?.instrumentId || '');

    // Combine instrument IDs from chart tabs and visible instruments
    const visibleInstrumentIds = useMemo(() => {
        return visibleInstrumentsResponse?.data?.map((instrument: any) => instrument.id) || [];
    }, [visibleInstrumentsResponse?.data]);

    const tabInstrumentIds = useMemo(() => {
        return chartTabs.map(tab => tab.instrumentId);
    }, [chartTabs]);

    const allInstrumentIds = useMemo(() => {
        return [...new Set([...tabInstrumentIds, ...visibleInstrumentIds])];
    }, [tabInstrumentIds, visibleInstrumentIds]);

    // Validate saved tabs when instruments load — not when the user opens a new tab
    useEffect(() => {
        if (!isInitialized || !visibleInstrumentsResponse?.data) return;

        const availableInstrumentIds = new Set(
            visibleInstrumentsResponse.data.map(inst => inst.id)
        );

        setChartTabs(currentTabs => {
            if (currentTabs.length === 0) return currentTabs;

            const invalidTabs = currentTabs.filter(
                tab => !availableInstrumentIds.has(tab.instrumentId)
            );
            if (invalidTabs.length === 0) return currentTabs;

            const validTabs = currentTabs.filter(tab =>
                availableInstrumentIds.has(tab.instrumentId)
            );

            if (validTabs.length > 0) {
                setActiveTab(prevActive => {
                    const nextActive = validTabs.some(tab => tab.id === prevActive)
                        ? prevActive
                        : validTabs[0].id;
                    saveTradingTabsState({ tabs: validTabs, activeTab: nextActive });
                    return nextActive;
                });
                return validTabs;
            }

            const firstInstrument = visibleInstrumentsResponse.data[0];
            const newTab = {
                id: `tab-${Date.now()}`,
                instrumentId: firstInstrument.id,
                symbol: firstInstrument.symbol,
            };
            setActiveTab(newTab.id);
            saveTradingTabsState({ tabs: [newTab], activeTab: newTab.id });
            return [newTab];
        });
    }, [visibleInstrumentsResponse?.data, isInitialized]);

    // Create initial tabs when instruments are loaded and no tabs exist
    useEffect(() => {
        if (
            isInstrumentsLoading ||
            !visibleInstrumentsResponse?.data ||
            visibleInstrumentsResponse.data.length === 0
        ) {
            return;
        }

        // Only create tabs if we don't have any yet
        if (chartTabs.length === 0) {
            const firstInstrument = visibleInstrumentsResponse.data[0];
            const newTab = {
                id: `tab-${Date.now()}`,
                instrumentId: firstInstrument.id,
                symbol: firstInstrument.symbol,
            };

            console.log('Creating initial tab with real instrument:', newTab);
            console.log(
                'Available instruments:',
                visibleInstrumentsResponse.data.map(inst => ({ id: inst.id, symbol: inst.symbol }))
            );
            setChartTabs([newTab]);
            setActiveTab(newTab.id);

            // Save the new state
            saveTradingTabsState({ tabs: [newTab], activeTab: newTab.id });
        }
    }, [visibleInstrumentsResponse, isInstrumentsLoading, chartTabs.length, saveTradingTabsState]);

    // Get current instrument price (priority fetch for buy/sell buttons) - faster polling
    const {
        price: currentInstrumentPrice,
        isLoading: isCurrentPriceLoading,
        error: currentPriceError,
    } = useCurrentInstrumentPrice(currentPair?.instrumentId || ''); // updated via WebSocket (usePriceSocket)

    // Get all instrument prices for the list
    // This is for the instruments list display, not blocking buy/sell buttons
    const { prices: allPrices, isLoading: isPricesLoading, error: pricesError } = useAllPrices(); // updated via WebSocket (usePriceSocket)

    // Stabilize allPrices to prevent unnecessary re-renders
    const stableAllPrices = useMemo(() => {
        if (!allPrices || allPrices.size === 0) return null;

        // Convert Map to a stable object representation
        const pricesObj: Record<string, { bid: number; ask: number; symbol: string }> = {};
        allPrices.forEach((priceData, instrumentId) => {
            pricesObj[instrumentId] = {
                bid: priceData.bid,
                ask: priceData.ask,
                symbol: priceData.symbol,
            };
        });

        return pricesObj;
    }, [allPrices]);

    // Market status - prefer backend flag from detail or instruments list
    const listInstrument = visibleInstrumentsResponse?.data?.find(
        inst => inst.id === currentPair?.instrumentId
    );
    const marketStatusSource = currentInstrument ?? listInstrument;
    const isWithinTradingHours = marketStatusSource?.is_within_trading_hours;

    const marketStatus = marketStatusSource
        ? isWithinTradingHours !== undefined
            ? {
                  is_trading_open: isWithinTradingHours,
                  status_text: isWithinTradingHours ? 'Market Open' : 'Market Closed',
                  last_update: new Date().toISOString(),
                  market_info: undefined,
                  next_opening: undefined,
                  next_closing: undefined,
              }
            : getInstrumentMarketStatus(marketStatusSource)
        : {
              is_trading_open: false,
              status_text: 'Market Closed',
              last_update: new Date().toISOString(),
              market_info: undefined,
              next_opening: undefined,
              next_closing: undefined,
          };

    const isMarketStatusLoading = false;
    const isMarketOpen = marketStatus.is_trading_open;
    // Translated market status text
    const displayMarketStatusText = (() => {
        if (marketStatus.is_trading_open) return t('marketOpenFull');
        if (marketStatus.next_opening) {
            const nextOpen = new Date(marketStatus.next_opening);
            const now = new Date();
            const diffMs = nextOpen.getTime() - now.getTime();
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            if (diffHours > 0) {
                return t('marketClosedOpensIn', {
                    hours: diffHours,
                    minutes: diffMinutes,
                });
            }
            if (diffMinutes > 0) {
                return t('marketClosedOpensInMinutes', { minutes: diffMinutes });
            }
            return t('marketClosedOpensSoon');
        }
        return t('marketClosedFull');
    })();

    // Update real-time prices from the efficient price service
    useEffect(() => {
        if (stableAllPrices && Object.keys(stableAllPrices).length > 0) {
            const newPrices: Record<string, { bid: number; ask: number; symbol: string }> = {};

            // Map prices by instrument ID
            allInstrumentIds.forEach(instrumentId => {
                const priceData = stableAllPrices[instrumentId];
                if (priceData) {
                    newPrices[instrumentId] = {
                        bid: priceData.bid,
                        ask: priceData.ask,
                        symbol: priceData.symbol || '',
                    };
                }
            });

            console.log(`Mapped ${Object.keys(newPrices).length} prices to instruments`);
            setRealTimePrices(newPrices);
        }
    }, [stableAllPrices, allInstrumentIds, isPricesLoading, pricesError]);

    // Clear changing pair state when current instrument price is available
    useEffect(() => {
        if (isChangingPair && currentInstrumentPrice) {
            // Clear loading state when we have price data for the current instrument
            setIsChangingPair(false);
        }
    }, [isChangingPair, currentInstrumentPrice]);

    // Also clear changing pair state after a maximum timeout as fallback
    useEffect(() => {
        if (isChangingPair) {
            const timeout = setTimeout(() => {
                setIsChangingPair(false);
            }, 2000); // Maximum 2 seconds
            return () => clearTimeout(timeout);
        }
        return undefined;
    }, [isChangingPair]);

    // Transform API orders to match OrderHistory component interface
    const transformOrdersForHistory = (apiOrders: any[]) => {
        return apiOrders.map(order => ({
            id: parseInt(order.id) || 0,
            pair: order.instrument?.symbol || order.symbol || '',
            type: order.side === 'buy' ? 'Buy' : 'Sell',
            amount: (order.lot_size || 0) * 100000, // Convert lots to units
            price: parseFloat(order.price) || 0,
            status:
                order.status === 'filled'
                    ? 'Filled'
                    : order.status === 'pending'
                      ? 'Pending'
                      : order.status === 'cancelled'
                        ? 'Cancelled'
                        : order.status,
            time: order.created_at || order.executed_at || new Date().toISOString(),
        }));
    };

    // Handle order success callback
    const handleOrderSuccess = () => {
        // Refresh data when order is successful
        refetchOrders();
        refetchBalance();
        setMobileActiveModal(null); // Close modal on mobile success
        setIsOrderFormDialogOpen(false); // Close modal on desktop success
    };

    const handleSelectPair = async (instrument: any) => {
        // If we receive a pair object from TradingPairs component
        const instrumentId = instrument.id || instrument.instrumentId;
        const symbol = instrument.symbol;

        const existingTab = chartTabs.find(tab => tab.instrumentId === instrumentId);

        if (existingTab) {
            // Switch to existing tab - show loading during transition
            setIsChangingPair(true);
            setActiveTab(existingTab.id);
        } else {
            // Create new tab - show loading during creation
            setIsChangingPair(true);
            const newTabId = `tab-${Date.now()}`;
            const newTab = {
                id: newTabId,
                instrumentId: instrumentId,
                symbol: symbol,
            };
            setChartTabs([...chartTabs, newTab]);
            setActiveTab(newTabId);
        }
    };

    const handleCloseTab = async (tabId: string) => {
        const newTabs = chartTabs.filter(tab => tab.id !== tabId);

        // If this would result in no tabs, create a new one with the first available instrument
        if (newTabs.length === 0 && allInstrumentIds.length > 0) {
            const firstInstrumentId = allInstrumentIds[0];
            const fallbackTab = {
                id: 'tab-1',
                instrumentId: firstInstrumentId,
                symbol: 'EUR/USD', // Default symbol since we only have IDs
            };
            setChartTabs([fallbackTab]);
            setActiveTab(fallbackTab.id);
            return;
        } else if (newTabs.length === 0) {
            // Don't create fallback tabs - wait for real instruments
            console.log('No instruments available, not creating fallback tab');
            return;
        }

        setChartTabs(newTabs);

        // If closing active tab, switch to first remaining tab
        if (activeTab === tabId) {
            setActiveTab(newTabs[0].id);
        }
    };

    // Save visibility state to localStorage
    useEffect(() => {
        localStorage.setItem('trading_sidebar_visible', isSidebarVisible.toString());
    }, [isSidebarVisible]);

    useEffect(() => {
        localStorage.setItem('trading_tabs_visible', isTabsVisible.toString());
    }, [isTabsVisible]);

    // Trigger chart resize when visibility changes
    useEffect(() => {
        // Wait for DOM to update after visibility change, then trigger multiple resize attempts
        const timers: NodeJS.Timeout[] = [];

        // Trigger resize at multiple intervals to catch the chart
        timers.push(setTimeout(() => window.dispatchEvent(new Event('resize')), 100));
        timers.push(setTimeout(() => window.dispatchEvent(new Event('resize')), 200));
        timers.push(setTimeout(() => window.dispatchEvent(new Event('resize')), 300));
        timers.push(setTimeout(() => window.dispatchEvent(new Event('resize')), 500));

        return () => {
            timers.forEach(timer => clearTimeout(timer));
        };
    }, [isSidebarVisible, isTabsVisible, isLandscape]);

    // Toggle handlers
    const toggleSidebar = () => {
        setIsSidebarVisible(prev => {
            // When showing sidebar, ensure it's visible
            if (!prev) {
                // Sidebar will use default width (lg:w-96 = 384px)
                return true;
            }
            return false;
        });
        // Trigger resize after state update
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 100);
    };

    const toggleTabs = () => {
        setIsTabsVisible(prev => !prev);
        // Trigger resize after state update
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 100);
    };

    if (systemStatus?.trading_enabled === false) {
        return (
            <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
                <div className="text-center space-y-4">
                    <div className="text-6xl">🚫</div>
                    <h1 className="text-2xl font-bold">{t('tradingTemporarilyDisabled')}</h1>
                    <p className="text-muted-foreground max-w-md">
                        {t('tradingUnavailableMessage')}
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                    >
                        {t('goToDashboard')}
                    </button>
                </div>
            </div>
        );
    }

    if (!isInitialized || chartTabs.length === 0) {
        return (
            <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
                <div className="flex items-center space-x-2">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>{t('loadingTradingInterface')}</span>
                </div>
            </div>
        );
    }

    // --- RENDER BLOCKS ---

    // Top Header - Shared
    const TopHeader = (
        <div className="bg-card border-b border-border px-2 sm:px-4 py-2 flex-shrink-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-2 lg:space-y-0">
                {/* Left Side - Hamburger and Market Status */}
                <div className="flex items-center justify-between lg:justify-start space-x-2 sm:space-x-6">
                    <button
                        onClick={() => navigate('/')}
                        className="p-2 hover:bg-accent rounded-md transition-colors"
                        title={t('goToHome')}
                    >
                        <Menu className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
                    </button>

                    <div className="flex items-center space-x-2 sm:space-x-4 text-xs sm:text-sm text-muted-foreground">
                        <span className="flex items-center">
                            {isMarketStatusLoading ? (
                                <>
                                    <Loader2 className="w-2 h-2 animate-spin mr-1 sm:mr-2" />
                                    <span className="hidden sm:inline">{t('checkingMarket')}</span>
                                    <span className="sm:hidden">{tCommon('loading')}</span>
                                </>
                            ) : (
                                <>
                                    <div
                                        className={`w-2 h-2 rounded-full mr-1 sm:mr-2 ${isMarketOpen ? 'bg-green-500' : 'bg-red-500'}`}
                                    ></div>
                                    <span className="hidden lg:inline">
                                        {displayMarketStatusText}
                                    </span>
                                    <span className="hidden sm:inline lg:hidden">
                                        {displayMarketStatusText}
                                    </span>
                                    <span className="sm:hidden">
                                        {isMarketOpen ? t('marketOpen') : t('marketClosed')}
                                    </span>
                                </>
                            )}
                        </span>
                    </div>
                </div>

                {/* Right Side - Balance Stats and Account Dropdown */}
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    {/* Balance Stats */}
                    <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
                        {(() => {
                            const selectedAccount = getSelectedAccount();
                            if (!selectedAccount) return null;

                            return (
                                <>
                                    <span className="text-muted-foreground">
                                        {t('balance')}{' '}
                                        <span className="text-foreground">
                                            {selectedAccount.base_currency}{' '}
                                            {Number(selectedAccount.balance).toFixed(2)}
                                        </span>
                                    </span>
                                    <span className="text-muted-foreground hidden sm:inline">
                                        {t('credit')}: <span className="text-foreground">0.00</span>
                                    </span>
                                    <span className="text-muted-foreground">
                                        {t('equity')}{' '}
                                        <span className="text-foreground">
                                            {selectedAccount.base_currency}{' '}
                                            {Number(selectedAccount.equity).toFixed(2)}
                                        </span>
                                    </span>
                                    <span className="text-muted-foreground hidden lg:inline">
                                        {t('margin')}{' '}
                                        <span className="text-foreground">
                                            {selectedAccount.base_currency}{' '}
                                            {Number(selectedAccount.used_margin).toFixed(2)}
                                        </span>
                                    </span>
                                    <span className="text-muted-foreground hidden xl:inline">
                                        {t('freeMargin')}{' '}
                                        <span className="text-foreground">
                                            {selectedAccount.base_currency}{' '}
                                            {Number(selectedAccount.free_margin).toFixed(2)}
                                        </span>
                                    </span>
                                    <span className="text-muted-foreground">
                                        {t('pl')}{' '}
                                        <span
                                            className={`${Number(selectedAccount.unrealized_pnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}
                                        >
                                            {selectedAccount.base_currency}{' '}
                                            {Number(selectedAccount.unrealized_pnl).toFixed(2)}
                                        </span>
                                    </span>
                                </>
                            );
                        })()}
                    </div>

                    {/* Trading Account Selector */}
                    <div className="flex items-center space-x-2 sm:space-x-3">
                        <span className="text-xs sm:text-sm text-muted-foreground">
                            {t('account')}:
                        </span>
                        <Select
                            value={selectedTradingAccount}
                            onValueChange={setSelectedTradingAccount}
                        >
                            <SelectTrigger className="w-32 sm:w-40 lg:w-48 bg-background border-border text-foreground text-xs sm:text-sm">
                                <SelectValue placeholder={t('selectAccount')} />
                            </SelectTrigger>
                            <SelectContent className="bg-popover border-border">
                                {tradingAccountsData?.accounts.map(account => (
                                    <SelectItem
                                        key={account.id}
                                        value={account.id}
                                        className="text-foreground hover:bg-accent hover:text-accent-foreground"
                                    >
                                        {account.base_currency} - #{account.account_number || 'N/A'}{' '}
                                        - ${Number(account.balance).toFixed(2)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>
        </div>
    );

    // Desktop Layout
    const DesktopLayout = (
        <div className="hidden lg:flex flex-col h-[calc(100vh-60px)] overflow-hidden">
            {/* Mobile: Stack vertically, Desktop: Side by side */}
            <div className="flex flex-col lg:flex-row flex-1 min-h-0 min-w-0 relative overflow-hidden">
                {/* Toggle icon for sidebar - shown when sidebar is hidden, positioned on chart side so fully visible */}
                {!isSidebarVisible && (
                    <button
                        onClick={toggleSidebar}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 bg-card border border-border rounded-full hover:bg-accent transition-colors z-20 shadow-sm lg:block hidden"
                        title="Show sidebar"
                    >
                        <ChevronLeft className="h-3 w-3 text-foreground" />
                    </button>
                )}

                {/* Chart Section */}
                <div className="flex-1 flex flex-col min-h-0 min-w-0 lg:min-h-[60vh] relative overflow-hidden">
                    {/* Chart with Tabs */}
                    <div className="flex-1 p-1 sm:p-2 lg:p-4 min-h-0 min-w-0 overflow-hidden">
                        <Card className="h-full w-full bg-card border-border overflow-hidden">
                            <Tabs
                                value={activeTab}
                                onValueChange={setActiveTab}
                                className="h-full flex flex-col"
                            >
                                <TabsList className="bg-muted border-b border-border rounded-none justify-start h-10 p-0 overflow-x-auto">
                                    {chartTabs.map(tab => (
                                        <div
                                            key={tab.id}
                                            className="flex items-center flex-shrink-0"
                                        >
                                            <TabsTrigger
                                                value={tab.id}
                                                className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground hover:text-foreground px-4 py-2 h-10 rounded-none text-sm"
                                            >
                                                {tab.symbol}
                                            </TabsTrigger>
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleCloseTab(tab.id);
                                                }}
                                                className="ml-1 mr-2 p-1 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                </TabsList>

                                {chartTabs.map(tab => (
                                    <TabsContent
                                        key={tab.id}
                                        value={tab.id}
                                        className="flex-1 m-0 p-0 min-h-0 min-w-0 overflow-hidden"
                                    >
                                        <div className="w-full h-full min-h-0 min-w-0">
                                            <KLineProChart
                                                key={`${tab.id}-${isSidebarVisible}-${isTabsVisible}`}
                                                instrumentId={tab.instrumentId}
                                                symbol={tab.symbol}
                                            />
                                        </div>
                                    </TabsContent>
                                ))}
                            </Tabs>
                        </Card>
                    </div>

                    {/* Trading Activity - Bottom on mobile, right on desktop */}
                    {isTabsVisible && (
                        <div
                            className="relative p-2 lg:p-4 border-t border-border flex-shrink-0 overflow-visible"
                            style={{ height: '300px' }}
                        >
                            {/* Toggle icon for tabs - on the border when visible, positioned on tabs side so fully visible */}
                            <button
                                onClick={toggleTabs}
                                className="absolute left-1/2 -translate-x-1/2 top-2 p-1 bg-card border border-border rounded-full hover:bg-accent transition-colors z-20 shadow-sm"
                                title="Hide trading activity"
                            >
                                <ChevronUp className="h-3 w-3 text-foreground" />
                            </button>
                            <div className="h-full overflow-auto">
                                <TradingActivity
                                    orders={transformOrdersForHistory(ordersData?.data || [])}
                                />
                            </div>
                        </div>
                    )}

                    {/* Toggle icon for tabs - on the border when hidden, positioned on chart side so fully visible */}
                    {!isTabsVisible && (
                        <button
                            onClick={toggleTabs}
                            className="absolute left-1/2 -translate-x-1/2 bottom-2 p-1 bg-card border border-border rounded-full hover:bg-accent transition-colors z-20 shadow-sm"
                            title="Show trading activity"
                        >
                            <ChevronDown className="h-3 w-3 text-foreground" />
                        </button>
                    )}
                </div>

                {/* Right Panel - Order Form and Trading Pairs */}
                {isSidebarVisible && (
                    <div className="relative w-full lg:w-96 lg:flex-shrink-0 lg:flex flex-col lg:border-l border-border overflow-visible min-w-0">
                        {/* Toggle icon for sidebar - on the border when visible, positioned on sidebar side so fully visible */}
                        <button
                            onClick={toggleSidebar}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-1 bg-card border border-border rounded-full hover:bg-accent transition-colors z-20 shadow-sm lg:block hidden"
                            title="Hide sidebar"
                        >
                            <ChevronRight className="h-3 w-3 text-foreground" />
                        </button>
                        {/* Inner container to handle overflow */}
                        <div className="w-full h-full overflow-hidden flex flex-col">
                            {/* Order Form */}
                            <div className="p-2 lg:p-4 border-b border-border">
                                {(() => {
                                    const currentPair = getCurrentPair();

                                    if (!currentPair) {
                                        if (isInstrumentsLoading) {
                                            return (
                                                <div className="p-4 text-center text-muted-foreground">
                                                    <div className="text-sm">
                                                        Loading instruments...
                                                    </div>
                                                </div>
                                            );
                                        } else if (
                                            visibleInstrumentsResponse?.data &&
                                            visibleInstrumentsResponse.data.length === 0
                                        ) {
                                            return (
                                                <div className="p-4 text-center text-muted-foreground">
                                                    <div className="text-sm">
                                                        No instruments available
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <div className="p-4 text-center text-muted-foreground">
                                                    <div className="text-sm">
                                                        Initializing trading...
                                                    </div>
                                                </div>
                                            );
                                        }
                                    }

                                    // Use current instrument price (priority) or fallback to allPrices
                                    const currentPrice =
                                        currentInstrumentPrice ||
                                        (currentPair?.instrumentId
                                            ? {
                                                  bid:
                                                      allPrices.get(currentPair.instrumentId)
                                                          ?.bid || 0,
                                                  ask:
                                                      allPrices.get(currentPair.instrumentId)
                                                          ?.ask || 0,
                                                  symbol: currentPair.symbol,
                                              }
                                            : undefined);

                                    return (
                                        <OrderForm
                                            selectedPair={currentPair.symbol}
                                            selectedInstrumentId={currentPair.instrumentId}
                                            realTimePrice={currentPrice}
                                            selectedAccount={getSelectedAccount()}
                                            onOrderSuccess={handleOrderSuccess}
                                            isPricesLoading={
                                                isCurrentPriceLoading && !currentInstrumentPrice
                                            }
                                            pricesError={currentPriceError || pricesError}
                                            instrumentType={
                                                (currentInstrument?.instrument_type
                                                    ?.primary_type as any) || 'forex'
                                            }
                                            instrumentData={
                                                currentInstrument
                                                    ? {
                                                          min_lot: currentInstrument.min_lot,
                                                          max_lot: currentInstrument.max_lot,
                                                          max_leverage:
                                                              currentInstrument.max_leverage,
                                                          leverage: currentInstrument.leverage,
                                                      }
                                                    : undefined
                                            }
                                            onOpenDialog={direction => {
                                                setSelectedDirection(direction);
                                                setIsOrderFormDialogOpen(true);
                                            }}
                                        />
                                    );
                                })()}
                            </div>

                            {/* Trading Pairs */}
                            <div className="flex-1 min-h-0 min-w-0 p-2 lg:p-4 overflow-hidden">
                                <TradingPairs
                                    selectedInstrumentId={currentPair?.instrumentId || ''}
                                    onSelectPair={handleSelectPair}
                                    realTimePrices={realTimePrices}
                                    isPricesLoading={isPricesLoading}
                                    pricesError={pricesError}
                                    marketStatuses={{}}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Desktop Dialogs */}
            {currentPair && (
                <OrderFormDialog
                    isOpen={isOrderFormDialogOpen}
                    onClose={() => setIsOrderFormDialogOpen(false)}
                    selectedPair={currentPair.symbol}
                    selectedInstrumentId={currentPair.instrumentId}
                    realTimePrice={
                        currentInstrumentPrice ||
                        (currentPair?.instrumentId
                            ? {
                                  bid: allPrices.get(currentPair.instrumentId)?.bid || 0,
                                  ask: allPrices.get(currentPair.instrumentId)?.ask || 0,
                                  symbol: currentPair.symbol,
                              }
                            : undefined)
                    }
                    selectedAccount={getSelectedAccount()}
                    onOrderSuccess={handleOrderSuccess}
                    isPricesLoading={isCurrentPriceLoading && !currentInstrumentPrice}
                    pricesError={currentPriceError || pricesError}
                    instrumentType={
                        (currentInstrument?.instrument_type?.primary_type as any) || 'forex'
                    }
                    instrumentData={
                        currentInstrument
                            ? {
                                  min_lot: currentInstrument.min_lot,
                                  max_lot: currentInstrument.max_lot,
                                  max_leverage: currentInstrument.max_leverage,
                                  leverage: currentInstrument.leverage,
                              }
                            : undefined
                    }
                    direction={selectedDirection}
                />
            )}
        </div>
    );

    // Mobile Layout
    const MobileLayout = (
        <div className="lg:hidden flex flex-col h-[calc(100vh-120px)] relative">
            <AnimatePresence>
                {isLandscape && currentPair && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-background"
                    >
                        <div className="w-full h-full relative">
                            {/* Close landscape button */}
                            <button
                                className="absolute top-2 right-2 z-50 p-2 bg-background/50 rounded-full"
                                onClick={() => {
                                    /* Consider adding a way to exit landscape without rotation if needed? Actually rotation handles it. */
                                }}
                            >
                                {/* Optional UI overlay for landscape */}
                            </button>
                            <KLineProChart
                                key={`${currentPair.instrumentId}-landscape`}
                                instrumentId={currentPair.instrumentId}
                                symbol={currentPair.symbol}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {!isLandscape && (
                <>
                    {/* Top Chart Area */}
                    <div className="h-[40vh] w-full bg-card border-b border-border flex-shrink-0 relative">
                        {currentPair ? (
                            <KLineProChart
                                key={`${currentPair.instrumentId}-mobile`}
                                instrumentId={currentPair.instrumentId}
                                symbol={currentPair.symbol}
                            />
                        ) : (
                            <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                                {t('selectPair')}
                            </div>
                        )}
                    </div>

                    {/* Scrollable Instruments */}
                    <div className="flex-1 overflow-hidden relative">
                        <div className="absolute inset-0">
                            <TradingPairs
                                selectedInstrumentId={currentPair?.instrumentId || ''}
                                onSelectPair={handleSelectPair}
                                realTimePrices={realTimePrices}
                                isPricesLoading={isPricesLoading}
                                pricesError={pricesError}
                                marketStatuses={{}}
                            />
                        </div>
                    </div>

                    {/* Bottom Navigation */}
                    <div className="h-16 border-t border-border bg-card flex items-center justify-around px-4 flex-shrink-0 pb-safe">
                        <Button
                            variant="default"
                            className="flex-1 mx-1 bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => {
                                setSelectedDirection('buy');
                                setMobileActiveModal('buy');
                            }}
                        >
                            <TrendingUp className="mr-2 h-4 w-4" />
                            {t('buy')}
                        </Button>
                        <Button
                            variant="default"
                            className="flex-1 mx-1 bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => {
                                setSelectedDirection('sell');
                                setMobileActiveModal('sell');
                            }}
                        >
                            <TrendingDown className="mr-2 h-4 w-4" />
                            {t('sell')}
                        </Button>
                        <Button
                            variant="outline"
                            className="ml-2"
                            onClick={() => setMobileActiveModal('trades')}
                        >
                            <History className="mr-2 h-4 w-4" />
                            {t('trades')}
                        </Button>
                    </div>
                </>
            )}

            {/* Mobile Bottom Sheets */}

            {/* Buy/Sell Form */}
            <MobileBottomSheet
                isOpen={mobileActiveModal === 'buy' || mobileActiveModal === 'sell'}
                onClose={() => setMobileActiveModal(null)}
                title={
                    mobileActiveModal === 'buy'
                        ? `${t('buy')} ${currentPair?.symbol || ''}`
                        : `${t('sell')} ${currentPair?.symbol || ''}`
                }
            >
                <div className="p-4">
                    {(() => {
                        // Reuse the OrderForm logic context
                        const currentPair = getCurrentPair();
                        if (!currentPair) return null;

                        const currentPrice =
                            currentInstrumentPrice ||
                            (currentPair?.instrumentId
                                ? {
                                      bid: allPrices.get(currentPair.instrumentId)?.bid || 0,
                                      ask: allPrices.get(currentPair.instrumentId)?.ask || 0,
                                      symbol: currentPair.symbol,
                                  }
                                : undefined);

                        return (
                            <OrderFormContent
                                selectedPair={currentPair.symbol}
                                selectedInstrumentId={currentPair.instrumentId}
                                realTimePrice={currentPrice}
                                selectedAccount={getSelectedAccount()}
                                onOrderSuccess={handleOrderSuccess}
                                isPricesLoading={isCurrentPriceLoading && !currentInstrumentPrice}
                                pricesError={currentPriceError || pricesError}
                                instrumentType={
                                    (currentInstrument?.instrument_type?.primary_type as any) ||
                                    'forex'
                                }
                                instrumentData={
                                    currentInstrument
                                        ? {
                                              min_lot: currentInstrument.min_lot,
                                              max_lot: currentInstrument.max_lot,
                                              max_leverage: currentInstrument.max_leverage,
                                              leverage: currentInstrument.leverage,
                                          }
                                        : undefined
                                }
                                direction={mobileActiveModal === 'buy' ? 'buy' : 'sell'}
                                onClose={() => setMobileActiveModal(null)}
                            />
                        );
                    })()}
                </div>
            </MobileBottomSheet>

            {/* Trades (Positions/Orders/History) */}
            <MobileBottomSheet
                isOpen={mobileActiveModal === 'trades'}
                onClose={() => setMobileActiveModal(null)}
                title={t('trades')}
            >
                <div className="h-full p-2">
                    <TradingActivity
                        orders={transformOrdersForHistory(ordersData?.data || [])}
                        defaultTab="positions"
                    />
                </div>
            </MobileBottomSheet>
        </div>
    );

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col">
            {TopHeader}
            {DesktopLayout}
            {MobileLayout}
        </div>
    );
};

export default TradingPage;
